import { useCallback, useState, type MouseEvent, type ReactNode } from 'react';

export interface Size2MixSectionProps {
    title: ReactNode;
    /** Optional material icon shown before the title. */
    icon?: string;
    /** Controlled open state (pair with `useSectionsToggle`). */
    isOpen?: boolean;
    defaultOpen?: boolean;
    onToggle?: (open: boolean) => void;
    /** Right-aligned content of the section header (buttons, toggles…). */
    actions?: ReactNode;
    children: ReactNode;
    /** Inner padding of the body (default: FDS accordion padding 16px 24px). */
    bodyPadding?: number | string;
    togglePosition?: 'leading' | 'trailing';
}

/**
 * Collapsible white block — Nemo renders every page section as
 * `details.nj-accordion-item.nj-accordion-item--leading-toggle.mt-3` (white, chevron on the left, 16px bold label).
 * Wrap a group of sections in `<Size2MixAccordion>` (or `<Size2MixExpandCollapseBar>`) to get the FDS spacing.
 */
export function Size2MixSection({ title, icon, isOpen, defaultOpen = true, onToggle, actions, children, bodyPadding, togglePosition = 'leading' }: Size2MixSectionProps) {
    const [internalOpen, setInternalOpen] = useState(defaultOpen);
    const open = isOpen ?? internalOpen;
    const toggle = (e: MouseEvent) => {
        e.preventDefault();
        if (isOpen === undefined) setInternalOpen(!open);
        onToggle?.(!open);
    };

    return (
        <details open={open} className={`nj-accordion-item${togglePosition === 'leading' ? ' nj-accordion-item--leading-toggle' : ''}`} style={{ marginTop: 12 }}>
            <summary onClick={toggle}>
                <span className="nj-accordion-item__label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {icon && (
                        <span className="material-icons nj-icon-material nj-icon-material--brand" aria-hidden="true" style={{ fontSize: 24 }}>
                            {icon}
                        </span>
                    )}
                    {title}
                </span>
                {actions && (
                    <span onClick={(e) => e.stopPropagation()} style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto', fontWeight: 400 }}>
                        {actions}
                    </span>
                )}
                <span className="material-icons nj-icon-material nj-accordion-item__toggle" aria-hidden="true">
                    expand_more
                </span>
            </summary>
            <div className="nj-accordion-item__content" style={bodyPadding !== undefined ? { padding: bodyPadding } : undefined}>
                {children}
            </div>
        </details>
    );
}

/** `div.nj-accordion` wrapper for a stack of `Size2MixSection`s. */
export function Size2MixAccordion({ children }: { children: ReactNode }) {
    return <div className="nj-accordion">{children}</div>;
}

/** Controlled open-state for a set of sections + "Tout développer / Tout réduire". */
export function useSectionsToggle<Id extends string>(ids: readonly Id[], initiallyOpen = true) {
    const [openMap, setOpenMap] = useState<Record<string, boolean>>(() => Object.fromEntries(ids.map((id) => [id, initiallyOpen])));
    const setAll = useCallback((open: boolean) => setOpenMap(Object.fromEntries(ids.map((id) => [id, open]))), [ids]);
    const bind = (id: Id) => ({
        isOpen: openMap[id] ?? initiallyOpen,
        onToggle: (open: boolean) => setOpenMap((m) => ({ ...m, [id]: open })),
    });
    return { bind, expandAll: () => setAll(true), collapseAll: () => setAll(false) };
}

/**
 * "Tout Développer / Tout Réduire" bar — FDS `.nj-accordion__actions` (outlined blue buttons, right-aligned).
 * Must be rendered inside a `Size2MixAccordion` to get the FDS button style.
 */
export function Size2MixExpandCollapseBar({ onExpandAll, onCollapseAll, children }: { onExpandAll: () => void; onCollapseAll: () => void; children?: ReactNode }) {
    return (
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
            {children && <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8 }}>{children}</div>}
            <div className="nj-accordion">
                <div className="nj-accordion__actions">
                    <button type="button" className="nj-btn nj-accordion__action" onClick={onExpandAll}>
                        Tout Développer
                    </button>
                    <button type="button" className="nj-btn nj-accordion__action" onClick={onCollapseAll}>
                        Tout Réduire
                    </button>
                </div>
            </div>
        </div>
    );
}

/**
 * Label / value rows used inside sections (e.g. "Informations du réseau"):
 * 64px rows, label 15% wide in grey-500 15px, value offset by 48px.
 */
export function Size2MixInfoGrid({ rows }: { rows: Array<{ label: string; value: ReactNode }>; labelWidth?: number }) {
    return (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
            {rows.map((r) => (
                <div key={r.label} style={{ display: 'flex', flexDirection: 'row', minHeight: 64, alignItems: 'center' }}>
                    <div className="size2mix-info-label" style={{ flex: '0 0 calc((100% - 48px) * 0.15)', color: 'var(--nj-core-color-grey-500)', fontSize: 15, minHeight: 32, display: 'flex', alignItems: 'center' }}>{r.label}</div>
                    <div style={{ marginLeft: 48, flex: 1, fontSize: 16, minHeight: 32, display: 'flex', alignItems: 'center' }}>{typeof r.value === 'string' ? <div style={{ minWidth: 64, lineHeight: '32px' }}>{r.value}</div> : r.value}</div>
                </div>
            ))}
        </div>
    );
}

/**
 * Page title row as on Nemo list pages: `nj-text--lg` bold title (+ inline badges) and right-aligned solid buttons.
 * Network sub-pages with tabs (Topologie, Production, Plannings…) have NO page title — use `Size2MixTabs` directly.
 */
export function Size2MixPageHeader({
    title,
    icon,
    badges,
    subtitle,
    actions,
}: {
    title: ReactNode;
    icon?: ReactNode;
    /** Rendered inline after the title (e.g. NJBadge counters). */
    badges?: ReactNode;
    subtitle?: ReactNode;
    actions?: ReactNode;
}) {
    return (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0, flexWrap: 'wrap' }}>
                {icon}
                <p className="nj-text nj-text--lg" style={{ margin: 0, fontWeight: 700 }}>
                    {title}
                </p>
                {badges}
                {subtitle && <div style={{ flexBasis: '100%', fontSize: 14, color: 'var(--nj-semantic-color-text-neutral-secondary-default)' }}>{subtitle}</div>}
            </div>
            {actions && <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>{actions}</div>}
        </div>
    );
}
